import cn from "classnames";
import ServiceWorksPlanning from "components/modal/serviceWorksPlanning";
import { ServiceWorksTooltipContent } from "components/planning/serviceWorksTooltipContent";
import WithoutDriver from "components/shared/withoutDriver";
import { dateTimeToView } from "helpers/date";
import { formatNumber, ymEvent } from "helpers/system";
import Tooltip from "rc-tooltip";
import React, { useMemo } from "react";

const CarIcons = (props) => {
    const { item, modal, forwardingEnabled, organization, isTour } = props;
    const {
        carId,
        carPlanningComment,
        bidId,
        carNeedsMaintenanceComment,
        driverNeedsShiftChangeComment,
        carNeedsUrgentMaintenance,
        driverNeedsUrgentShiftChange,
        serviceWorks,
        bookingComment,
        driverShift,
        carDistanceFromLastPoint,
        routeDeviationDistance,
        carExternalOutput,
        isCarOnServicePoint,
    } = item;
    const carServiceWorks = serviceWorks ?? [];
    const openExternalDistributionBid = () => {
        ymEvent("ExternalDisBids");
        return modal({
            name: "externalDistributionBid",
            title: "Информация по внешнему заказу",
            windowMod: "wide",
            bidId,
            carId,
            organization,
        });
    };

    const serviceWorksContent = useMemo(
        () => <ServiceWorksTooltipContent serviceWorks={carServiceWorks} />,
        [carServiceWorks],
    );

    const carPlanningCommentOverlay = () => {
        const { createdBy, createdAt, comment } = carPlanningComment;
        return (
            <>
                {createdBy?.displayName && (
                    <div>
                        <strong className="pr-2">Автор:</strong>
                        <span>{createdBy.displayName}</span>
                    </div>
                )}
                {createdAt && (
                    <div>
                        <strong className="pr-2">Дата создания:</strong>
                        <span>{dateTimeToView(createdAt, "DD.MM.YYYY HH:mm (Z)")}</span>
                    </div>
                )}
                {comment && (
                    <div>
                        <strong className="pr-2">Комментарий:</strong>
                        <span>{comment}</span>
                    </div>
                )}
            </>
        );
    };

    const driverShiftContent = useMemo(() => {
        if (!driverShift) {
            return null;
        }

        const {
            plannedEndDateOfCurrentShift,
            plannedStartDateOfNextShift,
            address,
            currentDriverFullName,
            nextDriverFullName,
            createdAt,
            updatedAt,
            comment,
        } = driverShift;
        return (
            <>
                <strong>Запланирована пересменка</strong>
                <div>
                    <strong className="pr-2">Плановая дата окончания последней смены:</strong>
                    <span>{dateTimeToView(plannedEndDateOfCurrentShift, "DD.MM.YYYY HH:mm (Z)")}</span>
                </div>
                <div>
                    <strong className="pr-2">Плановая дата начала смены:</strong>
                    <span>{dateTimeToView(plannedStartDateOfNextShift, "DD.MM.YYYY HH:mm (Z)")}</span>
                </div>
                <div>
                    <strong className="pr-2">Локация пересменки:</strong>
                    <span>{address}</span>
                </div>
                <div>
                    <strong className="pr-2">ФИО водителя последней смены:</strong>
                    <span>{currentDriverFullName || "Не назначен"}</span>
                </div>
                <div>
                    <strong className="pr-2">ФИО водителя новой смены:</strong>
                    <span>{nextDriverFullName || "Не назначен"}</span>
                </div>
                <div>
                    <strong className="pr-2">Дата создания пересменки:</strong>
                    <span>{dateTimeToView(createdAt, "DD.MM.YYYY HH:mm (Z)")}</span>
                </div>
                <div>
                    <strong className="pr-2">Последняя дата редактирования пересменки:</strong>
                    <span>{dateTimeToView(updatedAt, "DD.MM.YYYY HH:mm (Z)")}</span>
                </div>
                {comment && (
                    <div>
                        <strong className="pr-2">Комментарий:</strong>
                        <span>{comment}</span>
                    </div>
                )}
            </>
        );
    }, [driverShift]);

    return (
        <div>
            {forwardingEnabled && carExternalOutput != null && (
                <Tooltip
                    destroyTooltipOnHide
                    overlayClassName="planning-table__body--tooltip"
                    overlay="Есть подходящий внешний заказ"
                    placement="bottom"
                    {...(isTour ? { visible: true } : {})}
                >
                    <div onClick={openExternalDistributionBid} className="d-flex align-items-center f-route__error">
                        <i className="pr-2 icon-uEAB5-truck-cost planning-table__body--external-bid-icon" />
                        от {formatNumber(carExternalOutput)} {organization?.currencyData?.name}/км
                    </div>
                </Tooltip>
            )}
            <div className="d-flex overflow-hidden align-items-center">
                {carDistanceFromLastPoint && (
                    <Tooltip
                        destroyTooltipOnHide
                        overlayClassName="planning-table__body--tooltip"
                        placement="bottom"
                        overlay="Расстояние между машиной и последней точкой заявки"
                    >
                        <span className="badge badge-danger mr-2">{formatNumber(carDistanceFromLastPoint)} км</span>
                    </Tooltip>
                )}
                {routeDeviationDistance && (
                    <Tooltip
                        destroyTooltipOnHide
                        overlayClassName="planning-table__body--tooltip"
                        placement="bottom"
                        overlay="Отклонение машины от маршрута"
                    >
                        <span className="badge badge-warning mr-2">
                            {formatNumber(routeDeviationDistance / 1000)} км
                        </span>
                    </Tooltip>
                )}
                {carPlanningComment?.comment && (
                    <Tooltip
                        destroyTooltipOnHide
                        overlayClassName="planning-table__body--tooltip"
                        overlay={carPlanningCommentOverlay}
                        placement="bottom"
                    >
                        <i className="icon-uEA88-info-circle text-info pr-2" />
                    </Tooltip>
                )}
                {carNeedsMaintenanceComment && (
                    <Tooltip
                        destroyTooltipOnHide
                        overlayClassName="planning-table__body--tooltip"
                        placement="bottom"
                        overlay={<WithoutDriver title={carNeedsMaintenanceComment} />}
                    >
                        <i
                            className={cn("pr-2 icon-uEA97-wrench", {
                                "text-muted": !carNeedsUrgentMaintenance,
                                "text-danger": carNeedsUrgentMaintenance,
                            })}
                        />
                    </Tooltip>
                )}
                {carServiceWorks.length > 0 && (
                    <Tooltip
                        destroyTooltipOnHide
                        overlayClassName="planning-table__body--tooltip-scroll"
                        placement="bottom"
                        overlay={serviceWorksContent}
                    >
                        <i
                            className={cn("pr-2 icon-uEA97-wrench text-muted cursor-pointer")}
                            onClick={(event) => {
                                event.stopPropagation();
                                modal({
                                    name: "default",
                                    title: "Сервисные работы",
                                    windowMod: "wide",
                                    CustomBody: ServiceWorksPlanning,
                                    bodyData: { serviceWorks: carServiceWorks },
                                });
                            }}
                        />
                    </Tooltip>
                )}
                {isCarOnServicePoint && (
                    <Tooltip
                        destroyTooltipOnHide
                        overlayClassName="planning-table__body--tooltip"
                        placement="bottom"
                        overlay="На сервисной точке"
                    >
                        <i className={cn("pr-2 icon-uEA97-wrench text-success")} />
                    </Tooltip>
                )}
                {(driverShift || driverNeedsShiftChangeComment) && (
                    <Tooltip
                        destroyTooltipOnHide
                        overlayClassName="planning-table__body--tooltip"
                        placement="bottom"
                        overlay={
                            driverShift ? driverShiftContent : <WithoutDriver title={driverNeedsShiftChangeComment} />
                        }
                    >
                        <i
                            className={cn("icon-uEAB2-people-arrows pr-2", {
                                "text-muted": !driverNeedsUrgentShiftChange,
                                "text-danger": driverNeedsUrgentShiftChange,
                            })}
                        />
                    </Tooltip>
                )}
                {bookingComment && (
                    <Tooltip
                        destroyTooltipOnHide
                        overlayClassName="planning-table__body--tooltip"
                        placement="bottom"
                        overlay={bookingComment}
                    >
                        <i className="icon-uEA81-lock text-muted" />
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

export default CarIcons;
